/* tslint:disable: max-line-length */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
	GoogleMap,
	MapInfoWindow,
	MapMarker,
	MapMarkerClusterer,
} from '@angular/google-maps';
import { Subscription } from 'rxjs';
import { MarkerInfo } from 'src/app/components/data-set/data-set-map.component';
import { UserProfile, MapData } from 'src/app/models/model';
import { ApiService } from 'src/app/services/api.services';

@Component({
	selector: 'app-device-map-home',
	templateUrl: './device-map-home.component.html',
	styleUrls: ['./device-map-home.component.scss'],
	imports: [GoogleMap, MapMarkerClusterer, MapMarker, MapInfoWindow],
})
export class DeviceMapHomeComponent implements OnInit, OnDestroy {
	@ViewChild('deviceGoogleMap', { static: false })
	map!: GoogleMap;
	@ViewChild(MapInfoWindow, { static: false })
	info!: MapInfoWindow;
	@ViewChild(MapMarkerClusterer, { static: false })
	markerClusterer!: MapMarkerClusterer;
	clusterInfoWindow = new google.maps.InfoWindow();

	subscription?: Subscription;
	currentMapZoom = 0;
	zoom = 10;
	center: google.maps.LatLngLiteral;
	options: google.maps.MapOptions = {
		zoomControl: false,
		scrollwheel: true,
		disableDoubleClickZoom: true,
	};

	userProfile?: UserProfile;
	defaultLat: number;
	defaultLng: number;
	mapVisibleDeviceCount: string;
	iconBase: string;
	icons: Record<string, { icon: string }>;

	markersInfo: MarkerInfo[] = [];
	infoContent = '';

	clusterIndex = 0;
	decreaseClusterIndexMethodRef: any;
	increaseClusterIndexMethodRef: any;
	clusterInfoWindowDomReadyListener: google.maps.MapsEventListener | null =
		null;
	clusterInfoWindowCloseClickListener: google.maps.MapsEventListener | null =
		null;

	clusterOfMarkers: google.maps.Marker[] = [];
	infoWindowBackArrow: HTMLElement | null = null;
	infoWindowForwardArrow: HTMLElement | null = null;

	clusterInfoWindowContent: string = '';

	constructor(private apiService: ApiService) {
		this.center = {
			lat: 0,
			lng: 0,
		};
		this.defaultLat = this.center.lat;
		this.defaultLng = this.center.lng;

		this.mapVisibleDeviceCount = '500';
		this.iconBase = 'assets/icons/';
		this.icons = {
			activeVisit: {
				icon: this.iconBase + 'icon-map-active-visit.svg',
			},
			unusedCode: {
				icon: this.iconBase + 'icon-map-unused-code.svg',
			},
			default: {
				icon: this.iconBase + 'icon-map-device.svg',
			},
		};
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
		this.removeClusterOfMarkerListeners();
	}

	removeClusterOfMarkerListeners(): void {
		this.infoWindowBackArrow?.removeEventListener(
			'click',
			this.decreaseClusterIndexMethodRef
		);
		this.infoWindowForwardArrow?.removeEventListener(
			'click',
			this.increaseClusterIndexMethodRef
		);
		this.clusterInfoWindowDomReadyListener?.remove();
		this.clusterInfoWindowCloseClickListener?.remove();
	}

	async ngOnInit(): Promise<void> {
		this.setClusterInfoWindowContent();
		this.decreaseClusterIndexMethodRef = this.decreaseClusterIndex.bind(this);
		this.increaseClusterIndexMethodRef = this.increaseClusterIndex.bind(this);

		this.setCenter();

		await this.apiService.getDashboardSettings().then((data) => {
			this.mapVisibleDeviceCount = data.mapVisibleDeviceCount;
		});

		this.subscription = this.map.mapClick.subscribe(() => {
			if (!!this.clusterInfoWindow) {
				this.clusterInfoWindow.close();
				this.removeClusterOfMarkerListeners();
			}
		});

		this.subscription = this.map.zoomChanged.subscribe(() => {
			if (!!this.clusterInfoWindow) {
				if (this.currentMapZoom > this.map.getZoom()!) {
					this.clusterInfoWindow.close();
					this.removeClusterOfMarkerListeners();
				}
			}

			if (this.map.getZoom()! <= 22) {
				this.currentMapZoom = this.map.getZoom()!;
			}
		});
	}

	setClusterInfoWindowContent(): void {
		this.clusterInfoWindowContent =
			'<div style="display: flex; align-items: center;">' +
			'<button id="backArrow" class="mat-icon-button" style="display: flex; align-items: center" data-automation-id="device-map-back-button">' +
			'<span class="material-icons" style="color: #C2D4DF; font-weight: 1000; font-size: xx-large">keyboard_arrow_left</span>' +
			'</button>' +
			'<div>' +
			`<div id="propertyDeviceInfo"></div>` +
			'</div>' +
			'<button id="forwardArrow" class="mat-icon-button" style="display: flex; align-items: center; justify-content: end;" data-automation-id="device-map-forward-button">' +
			'<span class="material-icons" style="color: #C2D4DF; font-weight: 1000; font-size: xx-large">keyboard_arrow_right</span>' +
			'</button>' +
			'</div>';

		this.clusterInfoWindow.setContent(this.clusterInfoWindowContent);
	}

	openInfo(marker: MapMarker): void {
		this.infoContent = this.createInfoWindowContent(marker.getTitle()!);
		this.info.open(marker);
	}

	getMarkerInfoFromTitle(markerTitle: string): MarkerInfo {
		const deviceSn = markerTitle.substr(
			markerTitle?.lastIndexOf('SN:') + 4,
			markerTitle.length - 1
		);
		return this.markersInfo.find((marker) => marker.deviceSn === deviceSn)!;
	}

	createInfoWindowContent(markerTitle: string): string {
		const {
			locality,
			locationName,
			postalCode,
			address1,
			deviceSn,
			deviceId,
			locationId,
		} = this.getMarkerInfoFromTitle(markerTitle);

		let locName = locationName;
		let serialNum = deviceSn;

		const urlStart = window.location.href.slice(
			0,
			window.location.href.indexOf('/')
		);

		locName =
			'<a data-automation-id="property-details-link" class="device-map-home-inner-html-1" href= "' +
			urlStart +
			'property-details/' +
			locationId +
			'">' +
			locName +
			'</a>';

		let locAddress =
			'<span  class="device-map-home-inner-html-2">' +
			address1 +
			'<br/>' +
			locality +
			', ' +
			postalCode +
			'</span>';

		serialNum =
			'<a data-automation-id="devices-link" class="device-map-home-inner-html-3" href= "' +
			urlStart +
			'devices/' +
			deviceId +
			'">' +
			'SN: ' +
			serialNum +
			'</a>';

		return locName + '<br/>' + locAddress + '<br/>' + serialNum;
	}

	openClusterMarkerInfo(cluster: any): void {
		this.clusterOfMarkers = cluster.markers;
		const isSamePropertyCluster = this.clusterOfMarkers.every(
			(marker) =>
				marker.getPosition()?.toString() ==
				this.clusterOfMarkers[0].getPosition()?.toString()
		);

		if (isSamePropertyCluster) {
			const markerTitle = this.clusterOfMarkers[this.clusterIndex].getTitle()!;
			this.clusterInfoWindow.setPosition(cluster.position);
			let anchor = new google.maps.MVCObject();
			anchor.set('anchorPoint', { g: true, x: 0, y: -30 });
			this.clusterInfoWindow.open(this.map.googleMap, anchor);

			this.clusterInfoWindowDomReadyListener =
				this.clusterInfoWindow.addListener('domready', () => {
					this.infoWindowBackArrow = document.getElementById('backArrow');
					this.infoWindowBackArrow?.addEventListener(
						'click',
						this.decreaseClusterIndexMethodRef
					);

					this.infoWindowForwardArrow = document.getElementById('forwardArrow');
					this.infoWindowForwardArrow?.addEventListener(
						'click',
						this.increaseClusterIndexMethodRef
					);

					this.updateInfoWindowContent();
				});

			this.clusterInfoWindowCloseClickListener =
				this.clusterInfoWindow.addListener('closeclick', () =>
					this.removeClusterOfMarkerListeners()
				);
		}
	}

	decreaseClusterIndex(): void {
		if (this.clusterIndex > 0) {
			this.clusterIndex--;
		} else {
			this.clusterIndex = this.clusterOfMarkers.length - 1;
		}
		this.updateInfoWindowContent();
	}

	increaseClusterIndex(): void {
		if (this.clusterIndex < this.clusterOfMarkers.length - 1) {
			this.clusterIndex++;
		} else {
			this.clusterIndex = 0;
		}
		this.updateInfoWindowContent();
	}

	updateInfoWindowContent(): void {
		const propertyDeviceInfo = document.getElementById('propertyDeviceInfo')!;
		propertyDeviceInfo.innerHTML = this.createInfoWindowContent(
			this.clusterOfMarkers[this.clusterIndex].getTitle()!
		);
	}

	// this gets called when the map is loaded
	boundsChanged(event: any): void {
		const bounds = this.map?.getBounds();
		const ne = bounds?.getNorthEast();
		const sw = bounds?.getSouthWest();

		this.getDevices();
	}

	async setCenter(): Promise<void> {
		this.userProfile = await this.apiService.getUserProfile();
		const activeCompanyLatLng =
			this.userProfile?.activeRoleDetails?.company?.address?.geoCoordinates;
		if (activeCompanyLatLng !== null && activeCompanyLatLng !== undefined) {
			this.center = {
				lat: activeCompanyLatLng?.latitude,
				lng: activeCompanyLatLng?.longitude,
			};
		} else {
			this.center = {
				lat: this.defaultLat,
				lng: this.defaultLng,
			};
		}
	}

	async getDevices(): Promise<void> {
		const bounds = this.map?.getBounds();
		const ne = bounds?.getNorthEast();
		const sw = bounds?.getSouthWest();
		let returnedData: MapData[]; // added this to make sure the array is empty. Some duplicate markers were showing on the map
		returnedData = await this.apiService.getDeviceMapData(
			ne?.lat(),
			ne?.lng(),
			sw?.lat(),
			sw?.lng()
		);
		// DE2977: Map # of devices showing wrong, this.markers" needs to be set to empty after the await, multiple calls could get
		// though clearing it before the await and then all pause at the await so they could all effectively get combined together,
		// with it after the await it will get clear by each one before getting populated due to the way the JS event loop works
		this.markersInfo = [];
		returnedData.forEach((element) => {
			const lat =
				element.device.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.address.geoCoordinates?.latitude !==
				undefined
					? element.device.activeDeviceAssignment?.activeInstallation
							?.installationLocation?.location?.address.geoCoordinates?.latitude
					: this.defaultLat;

			const lng =
				element.device.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.address.geoCoordinates
					?.longitude !== undefined
					? element.device.activeDeviceAssignment?.activeInstallation
							?.installationLocation?.location?.address.geoCoordinates
							?.longitude
					: this.defaultLng;

			const position = { lat, lng };

			const icon = element.deviceActivity.visitInProgress
				? this.icons['activeVisit'].icon
				: element.deviceActivity.unusedCode
					? this.icons['unusedCode'].icon
					: this.icons['default'].icon;

			const locationName =
				element.device.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.name ?? '';
			const address1 =
				element.device.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.address?.address1 ?? '';
			const locality =
				element.device.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.address?.locality ?? '';
			const postalCode =
				element.device.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.address?.postalCode ?? '';
			const deviceSn = element.device.serialNumber ?? '';
			const locationId =
				element.device.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.id ?? '';
			const deviceId = element.device.id ?? '';

			const title =
				locationName +
				'\n' +
				address1 +
				'\n' +
				locality +
				' ' +
				postalCode +
				'\n' +
				'SN: ' +
				deviceSn;

			const marker = {
				position,
				icon,
				title,
				clickable: true,
			};

			this.markersInfo.push({
				marker,
				deviceId,
				deviceSn,
				locationName,
				address1,
				locality,
				postalCode,
				locationId,
			});
		});
	}
}
